import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode
} from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollShadow } from '@heroui/react'
import clsx from 'clsx'
import { NavArrowRightIcon } from '@/ui/icons/nav-arrow-right'

interface ScrollableItemsProps {
  children: ReactNode
  enabled?: boolean
}

export const ScrollableItems = ({
  children,
  enabled = true
}: ScrollableItemsProps) => {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showHint, setShowHint] = useState(false)

  const updateHint = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const isScrollable = el.scrollHeight > el.clientHeight + 1
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8
    setShowHint(isScrollable && !atBottom)
  }, [])

  useLayoutEffect(() => {
    if (!enabled) return
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(updateHint)
    observer.observe(el)
    return () => observer.disconnect()
  }, [enabled, updateHint, children])

  if (!enabled) return <>{children}</>

  return (
    <div className="relative">
      <ScrollShadow
        ref={scrollRef}
        orientation="vertical"
        size={60}
        onScroll={updateHint}
        className="max-h-80 overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
      >
        {children}
      </ScrollShadow>
      <div
        aria-hidden
        className={clsx(
          'pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-2 transition-opacity duration-300',
          showHint ? 'opacity-100' : 'opacity-0'
        )}
      >
        <span className="flex items-center gap-1.5 rounded-full bg-neutral-800/90 px-3 py-1.5 text-xs font-medium text-white shadow-sm backdrop-blur-sm">
          {t('confirmation.scroll_to_see_more')}
          <NavArrowRightIcon className="size-3.5 rotate-90" />
        </span>
      </div>
    </div>
  )
}
